import { InfoCircledIcon } from '@radix-ui/react-icons';
import { IContractTemplateParagraph } from '@clickersphere/core/services/ContractTemplateService';
import Loader from '../../../Loader';
import Switch from '../../../Switch';
import TagVariablePopover from '../TagVariablePopover';
import { ICreateAnchorPayload } from '../useTemplateModalController';
import { IEditableContractVariable } from '../VariableMappingEditor';
import { useDocumentVariableEditorController } from './useDocumentVariableEditorController';

interface IDocumentVariableEditorProps {
  paragraphs: IContractTemplateParagraph[];
  variables: IEditableContractVariable[];
  isLoading?: boolean;
  onCreateAnchor: (payload: ICreateAnchorPayload) => void;
  onRemoveAnchorByMatch: (
    varKey: string,
    paragraphIndex: number,
    matchText: string,
  ) => void;
}

export default function DocumentVariableEditor({
  paragraphs,
  variables,
  isLoading,
  onCreateAnchor,
  onRemoveAnchorByMatch,
}: IDocumentVariableEditorProps) {
  const {
    t,
    selection,
    reviewAnchor,
    isPreviewFilled,
    renderedParagraphs,
    handleSelectionEnd,
    handleConfirmAnchor,
    handleCloseSelection,
    handleMarkClick,
    handleCloseReview,
    handleRemoveReviewAnchor,
    handleChangeReviewVariable,
    handleTogglePreview,
  } = useDocumentVariableEditorController({
    paragraphs,
    variables,
    onCreateAnchor,
    onRemoveAnchorByMatch,
  });

  if (isLoading) {
    return (
      <div className="flex flex-col items-center gap-2 py-8 text-center">
        <Loader className="h-8 w-8" />
        <span className="text-text-secondary text-sm">
          {t('contract.templates.editor.loadingDocument')}
        </span>
      </div>
    );
  }

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <p className="text-text-secondary text-sm">
          {t('contract.templates.editor.instruction')}
        </p>
        <span className="text-text-tertiary flex items-center gap-1 text-xs">
          <InfoCircledIcon className="size-3.5 shrink-0" />
          {t('contract.templates.editor.simplifiedNotice')}
        </span>
      </div>

      {renderedParagraphs.length > 0 && (
        <Switch
          id="preview-filled"
          checked={isPreviewFilled}
          onCheckedChange={handleTogglePreview}
          label={t('contract.templates.editor.previewToggle')}
        />
      )}

      {renderedParagraphs.length === 0 && (
        <div className="flex flex-col gap-1 py-4">
          <p className="text-text-secondary text-sm">
            {t('contract.templates.editor.emptyDocument')}
          </p>
          <p className="text-text-tertiary text-sm">
            {t('contract.templates.editor.emptyDocumentHint')}
          </p>
        </div>
      )}

      {renderedParagraphs.length > 0 && (
        <div
          data-editor-document
          className="border-border-default bg-surface-elevated flex max-h-[45vh] flex-col gap-2 overflow-y-auto rounded-lg border p-3 text-sm leading-relaxed select-text md:max-h-[60vh] lg:max-h-[65vh]"
          onMouseUp={handleSelectionEnd}
          onTouchEnd={handleSelectionEnd}
          onPointerUp={handleSelectionEnd}
        >
          {renderedParagraphs.map((paragraph) =>
            paragraph.segments.every((segment) => segment.text.trim() === '') &&
            paragraph.segments.length === 1 &&
            !paragraph.segments[0].varKey ? (
              <div
                key={paragraph.index}
                data-paragraph-index={paragraph.index}
                className="h-2"
              />
            ) : (
              <p
                key={paragraph.index}
                data-paragraph-index={paragraph.index}
                className="text-text-primary"
              >
                {paragraph.segments.map((segment) => {
                  const { varKey } = segment;

                  if (!varKey) {
                    return <span key={segment.id}>{segment.text}</span>;
                  }

                  const varLabel = segment.varLabel ?? varKey;

                  return (
                    <mark
                      key={segment.id}
                      data-var-key={varKey}
                      role={isPreviewFilled ? undefined : 'button'}
                      tabIndex={isPreviewFilled ? undefined : 0}
                      title={varLabel}
                      className={
                        isPreviewFilled
                          ? 'rounded bg-green-100 px-1 font-medium text-green-900 dark:bg-green-500/25 dark:text-green-100'
                          : 'cursor-pointer rounded bg-green-200 px-0.5 text-green-900 hover:bg-green-300 dark:bg-green-500/30 dark:text-green-100 dark:hover:bg-green-500/40'
                      }
                      onClick={() =>
                        handleMarkClick(
                          varKey,
                          varLabel,
                          paragraph.index,
                          segment.text,
                        )
                      }
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleMarkClick(
                            varKey,
                            varLabel,
                            paragraph.index,
                            segment.text,
                          );
                        }
                      }}
                    >
                      {segment.text}
                    </mark>
                  );
                })}
              </p>
            ),
          )}
        </div>
      )}

      {selection && (
        <TagVariablePopover
          selection={selection}
          variables={variables}
          paragraphs={paragraphs}
          onConfirm={handleConfirmAnchor}
          onClose={handleCloseSelection}
        />
      )}

      {reviewAnchor && (
        <TagVariablePopover
          selection={{
            paragraphIndex: reviewAnchor.paragraphIndex,
            matchText: reviewAnchor.matchText,
          }}
          variables={variables}
          paragraphs={paragraphs}
          review={{
            varKey: reviewAnchor.varKey,
            varLabel: reviewAnchor.varLabel,
          }}
          onConfirm={handleChangeReviewVariable}
          onRemoveAnchor={handleRemoveReviewAnchor}
          onClose={handleCloseReview}
        />
      )}
    </section>
  );
}
